/**
 * Gap-diagram — berekend render-pad (layout_mode = "gap-diff",
 * roadmap-tijdreizen.md stap 3d). Toont de element-niveau-diff van de
 * actieve branch t.o.v. z'n directe voorganger (vorig plateau / parent-branch)
 * in drie tabbladen (Objecten / Relaties / Reïficaties), met oud→nieuw per
 * veld en groen/geel/rood per status.
 *
 * Interim: de branch om te diffen is de ACTIEVE branch. Zodra de Gap↔branch-
 * binding (stap 3c) er is, leest dit de from/to-branches van het Gap-object.
 */
import type { Diagram } from "@ae/domain";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  api,
  type BranchDiffConceptMeta,
  type BranchDiffEntity,
  type BranchDiffRelationMeta,
  type BranchDiffResponse,
} from "../../api/client";
import { GlossaryInfoButton } from "../../help/uiGlossary";
import { getRegistry, resolveBilingual } from "../../registry";
import { useSession } from "../../state/session";
import { useWorkspaceData } from "../../state/workspace";
import { RelationGlyph } from "../explorer/RelationGlyph";
import { GapModelCanvas } from "./GapModelCanvas";
import {
  attrInfo,
  conceptTypeLabel,
  deepResolveLang,
  isEmptyValue,
  isTbdPlaceholder,
  stereoLabels,
} from "./gapDiffFormat";
import { ZoomPan } from "./ZoomPan";

interface GapDiffCanvasProps {
  diagram: Diagram;
}

type TabKey = "objects" | "relations" | "reifications";

const STATUS_STYLE: Record<BranchDiffEntity["status"], string> = {
  added: "bg-green-100 text-green-800 border-green-300",
  changed: "bg-amber-100 text-amber-800 border-amber-300",
  deleted: "bg-red-100 text-red-800 border-red-300",
};
const STATUS_DOT: Record<BranchDiffEntity["status"], string> = {
  added: "#16a34a",
  changed: "#d97706",
  deleted: "#dc2626",
};

function fmt(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}

function relationMeta(e: BranchDiffEntity): BranchDiffRelationMeta | null {
  const m = e.meta as BranchDiffRelationMeta | undefined;
  return m && m.kind === "relation" ? m : null;
}

function conceptMeta(e: BranchDiffEntity): BranchDiffConceptMeta | null {
  const m = e.meta as BranchDiffConceptMeta | undefined;
  return m && m.kind === "concept" ? m : null;
}

/** Source/target-delta-waarde: { name, branch } i.p.v. rauwe concept-id. */
interface ConceptRefValue {
  name: { nl?: string; en?: string };
  branch: string | null;
  branchIsMain?: boolean;
}
function isConceptRef(v: unknown): v is ConceptRefValue {
  return typeof v === "object" && v !== null && "name" in v && "branch" in v;
}

export function GapDiffCanvas(_props: GapDiffCanvasProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  const [data, setData] = useState<BranchDiffResponse | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [tab, setTab] = useState<TabKey>("objects");
  // Visueel conceptmodel (default) ↔ de platte tabel-weergave.
  const [viewMode, setViewMode] = useState<"model" | "table">("model");

  useEffect(() => {
    let cancelled = false;
    if (!workspaceId || !branchId) return;
    setState("loading");
    api
      .branchDiff(workspaceId, branchId)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setState("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, branchId]);

  const statusLabel = useMemo(
    () => ({
      added: t("gapDiff.statusAdded"),
      changed: t("gapDiff.statusChanged"),
      deleted: t("gapDiff.statusDeleted"),
    }),
    [t],
  );

  // Cel-renderer voor een delta-waarde. Voor source/target tonen we de
  // objectnaam (per taal) + de branch waar díé versie leeft i.p.v. de id.
  // Net label voor de technische basis-velden; uitgesplitste attributen
  // (bv. "Risico.risk_label") houden hun eigen naam.
  const FIELD_LABELS: Record<string, string> = {
    type_id: t("gapDiff.fields.typeId"),
    name: t("gapDiff.fields.name"),
    description: t("gapDiff.fields.description"),
    stereotypes: t("gapDiff.fields.stereotypes"),
    source: t("gapDiff.fields.source"),
    target: t("gapDiff.fields.target"),
    label: t("gapDiff.fields.label"),
  };
  const fieldLabel = (field: string): string => FIELD_LABELS[field] ?? field;

  const renderCell = (field: string, v: unknown): string => {
    if ((field === "source" || field === "target") && isConceptRef(v)) {
      const nm = resolveBilingual(v.name, lang) || "?";
      const branchName = v.branchIsMain ? t("gapDiff.currentSituation") : v.branch;
      return branchName ? `${nm} (${branchName})` : nm;
    }
    if (field === "stereotypes") return stereoLabels(v, lang);
    // type_id-waarde → element-/relatie-type-label i.p.v. de rauwe id.
    if (field === "type_id" && typeof v === "string") {
      const reg = getRegistry();
      const et = reg.getElementType(v);
      if (et) return resolveBilingual(et.label, lang);
      const rt = reg.getRelationType(v);
      if (rt) return resolveBilingual(rt.label, lang);
      return v;
    }
    // Boolean → Ja/Nee (technische true/false vermijden).
    if (typeof v === "boolean") return v ? t("gapDiff.valueYes") : t("gapDiff.valueNo");
    // Lege waarde ({}/""/[]) → streepje i.p.v. "{}".
    if (isEmptyValue(v)) return "—";
    // Alle overige velden: diep bilingual-resolven (ook geneste bags in
    // attributes/description/name/label) en dan formatteren.
    return fmt(deepResolveLang(v, lang));
  };

  // Splits het `attributes`-veld uit naar één rij per attribuut (waarde direct
  // i.p.v. een JSON-bag), met stereotype-prefix op de veldnaam. Overige velden
  // blijven ongewijzigd. Voor "changed" tonen we alleen écht-gewijzigde attrs.
  const displayDeltas = (
    e: BranchDiffEntity,
  ): { field: string; before: unknown; after: unknown }[] => {
    // Resolutie-context werkt voor zowel objecten (concept-meta) als relaties
    // (relatie-meta) zodat ook relatie-attributen (bv. directed) hun label
    // krijgen via het relatie-type/-stereotype-schema.
    const cMeta = conceptMeta(e);
    const rMeta = relationMeta(e);
    const stereos = cMeta?.stereotypes ?? rMeta?.stereotypes ?? [];
    const typeId = cMeta?.typeId ?? rMeta?.typeId;
    // TBD/NTB betekent áltijd "nog niet ingevuld" → die velden altijd weglaten
    // (elke status). Een lege after-waarde laten we alleen bij een NIEUW object
    // weg; bij changed kan leeg een echte "verwijderd"-wijziging zijn.
    const skipEmpty = e.status === "added";
    const skipAfter = (after: unknown): boolean =>
      isTbdPlaceholder(after) || (skipEmpty && isEmptyValue(after));
    const out: { field: string; before: unknown; after: unknown }[] = [];
    for (const d of e.deltas) {
      if (d.field !== "attributes") {
        if (skipAfter(d.after)) continue;
        out.push({ field: d.field, before: d.before, after: d.after });
        continue;
      }
      const bb =
        d.before && typeof d.before === "object" ? (d.before as Record<string, unknown>) : {};
      const ab = d.after && typeof d.after === "object" ? (d.after as Record<string, unknown>) : {};
      const keys = [...new Set([...Object.keys(bb), ...Object.keys(ab)])].sort();
      for (const k of keys) {
        const before = bb[k] ?? null;
        const after = ab[k] ?? null;
        if (e.status === "changed" && JSON.stringify(before) === JSON.stringify(after)) continue;
        if (skipAfter(after)) continue;
        const info = attrInfo(k, stereos, typeId, lang);
        // Reference-attribuut (bv. attribute_type_ref) → naam van het
        // gerefereerde concept; enum-waarde → option_label (bilingual). Anders
        // de rauwe waarde (renderCell formatteert verder).
        const mapVal = (val: unknown): unknown => {
          if (typeof val === "string" && info.schema?.type?.startsWith("reference")) {
            const c = useWorkspaceData.getState().store.getConcept(val);
            if (c) return resolveBilingual(c.name, lang) || val;
          }
          if (typeof val === "string" && info.schema?.option_labels?.[val]) {
            return info.schema.option_labels[val];
          }
          return val;
        };
        out.push({ field: info.field, before: mapVal(before), after: mapVal(after) });
      }
    }
    return out;
  };

  // Kolomkoppen = branch-namen (tijdreizen): before = vorig plateau (parent),
  // after = huidige branch. Een main-branch tonen we als "Huidige situatie".
  const branchDisplay = (
    b: { name: string; isMain?: boolean } | null | undefined,
    fallback: string,
  ): string => (b ? (b.isMain ? t("gapDiff.currentSituation") : b.name) : fallback);
  const beforeLabel = branchDisplay(data?.parent, t("gapDiff.colBefore"));
  const afterLabel = branchDisplay(data?.branch, t("gapDiff.colAfter"));

  // Sharp Attribuut/Identifier horen genest bij hun concept (parent_id), niet
  // als losse object-rij op het Gap-diagram.
  const OWNED_SUB_ELEMENTS = new Set(["sharp.Attribute", "sharp.Identifier"]);
  const visibleObjects = (data?.concepts ?? []).filter(
    (e) => !OWNED_SUB_ELEMENTS.has(conceptMeta(e)?.typeId ?? ""),
  );

  // Gewijzigde Attribuut/Identifier genest onder hun eigenaar-Concept (parent_id),
  // net als de model-view. Carrier-rij als het Concept zelf ongewijzigd is.
  const subByParent = new Map<string, BranchDiffEntity[]>();
  const parentNameById = new Map<string, { nl?: string; en?: string }>();
  if (tab === "objects") {
    for (const e of data?.concepts ?? []) {
      const m = conceptMeta(e);
      if (!m || !OWNED_SUB_ELEMENTS.has(m.typeId)) continue;
      if (e.status === "changed" && displayDeltas(e).length === 0) continue;
      const pid = m.parentId ?? "";
      if (!pid) continue;
      if (m.parentName) parentNameById.set(pid, m.parentName);
      const list = subByParent.get(pid) ?? [];
      list.push(e);
      subByParent.set(pid, list);
    }
  }

  let objectRows = visibleObjects;
  if (tab === "objects" && subByParent.size > 0) {
    const present = new Set(visibleObjects.map((e) => e.logicalId));
    const carriers: BranchDiffEntity[] = [];
    for (const pid of subByParent.keys()) {
      if (present.has(pid)) continue;
      const pname = parentNameById.get(pid);
      carriers.push({
        logicalId: pid,
        status: "changed",
        meta: { kind: "concept", typeId: "", name: pname ?? {} },
        deltas: [],
      });
    }
    objectRows = [...visibleObjects, ...carriers];
  }

  const entities: BranchDiffEntity[] =
    tab === "objects" ? objectRows : tab === "relations" ? (data?.relations ?? []) : [];

  const tabs: { key: TabKey; label: string }[] = [
    { key: "objects", label: t("gapDiff.tabObjects") },
    { key: "relations", label: t("gapDiff.tabRelations") },
    { key: "reifications", label: t("gapDiff.tabReifications") },
  ];

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-white">
      <div className="flex items-center justify-between border-slate-200 border-b px-4 py-2">
        <div className="flex w-32 items-center">
          <GlossaryInfoButton
            termIds={[
              "gap",
              "plateau",
              "branch",
              "concept",
              "stereotype",
              "attribute",
              "identifier",
              "facet_reference",
              "concept_state",
            ]}
          />
        </div>
        <h2 className="font-semibold text-slate-700 text-sm">{t("gapDiff.title")}</h2>
        <div className="flex w-32 justify-end gap-1">
          <button
            type="button"
            onClick={() => setViewMode("model")}
            className={`rounded border px-2 py-0.5 text-[11px] ${
              viewMode === "model"
                ? "border-slate-300 bg-slate-100 font-semibold text-slate-800"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t("gapDiff.viewModel")}
          </button>
          <button
            type="button"
            onClick={() => setViewMode("table")}
            className={`rounded border px-2 py-0.5 text-[11px] ${
              viewMode === "table"
                ? "border-slate-300 bg-slate-100 font-semibold text-slate-800"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t("gapDiff.viewTable")}
          </button>
        </div>
      </div>

      {viewMode === "model" ? (
        <div className="flex-1 overflow-hidden">
          {state === "loading" && (
            <p className="p-4 text-slate-500 text-xs">{t("gapDiff.loading")}</p>
          )}
          {state === "error" && <p className="p-4 text-red-600 text-xs">{t("gapDiff.error")}</p>}
          {state === "ready" && data && <GapModelCanvas data={data} lang={lang} />}
        </div>
      ) : (
        <>
          <div className="flex gap-1 border-slate-200 border-b px-3 pt-2">
            {tabs.map((tb) => (
              <button
                key={tb.key}
                type="button"
                onClick={() => setTab(tb.key)}
                className={`rounded-t border border-b-0 px-3 py-1 text-xs ${
                  tab === tb.key
                    ? "border-slate-300 bg-white font-semibold text-slate-800"
                    : "border-transparent bg-slate-50 text-slate-500 hover:text-slate-700"
                }`}
              >
                {tb.label}
              </button>
            ))}
          </div>

          <ZoomPan className="flex-1" contentWidth="full">
            <div className="p-4">
              {state === "loading" && (
                <p className="text-slate-500 text-xs">{t("gapDiff.loading")}</p>
              )}
              {state === "error" && <p className="text-red-600 text-xs">{t("gapDiff.error")}</p>}
              {state === "ready" && tab === "reifications" && (
                <p className="text-slate-400 text-xs italic">{t("gapDiff.reificationsHint")}</p>
              )}
              {state === "ready" && tab !== "reifications" && entities.length === 0 && (
                <p className="text-slate-500 text-xs">{t("gapDiff.empty")}</p>
              )}
              {state === "ready" && tab !== "reifications" && entities.length > 0 && (
                <div className="flex flex-col gap-3">
                  {entities.map((e) => (
                    <div key={e.logicalId} className={`rounded border ${STATUS_STYLE[e.status]}`}>
                      <div className="flex items-center justify-between gap-2 px-2 py-1">
                        {(() => {
                          const rel = relationMeta(e);
                          if (rel) {
                            // Relatie-header: bron → glyph (met richting) → doel,
                            // analoog aan de Explorer-relatieweergave.
                            const src = rel.sourceLabel
                              ? resolveBilingual(rel.sourceLabel, lang)
                              : e.logicalId;
                            const dst = rel.targetLabel
                              ? resolveBilingual(rel.targetLabel, lang)
                              : "?";
                            return (
                              <span className="flex min-w-0 items-center gap-1.5 font-semibold text-xs">
                                <span className="truncate">{src}</span>
                                <span className="shrink-0" title={rel.typeId}>
                                  <RelationGlyph typeId={rel.typeId} attributes={rel.attributes} />
                                </span>
                                <span className="truncate">{dst}</span>
                              </span>
                            );
                          }
                          const obj = conceptMeta(e);
                          if (obj) {
                            // Object-header: naam (per UI-taal) + (type), analoog aan
                            // de Explorer. De platte e.label is NL-first; gebruik de
                            // bilingual meta.name zodat de header de canvas-taal volgt.
                            const objName = obj.name
                              ? resolveBilingual(obj.name, lang)
                              : (e.label ?? e.logicalId);
                            const objType = obj.typeId
                              ? conceptTypeLabel(obj.typeId, obj.stereotypes, lang)
                              : "";
                            return (
                              <span className="flex min-w-0 items-baseline gap-1 font-semibold text-xs">
                                <span className="truncate">{objName || e.logicalId}</span>
                                {objType && (
                                  <span className="shrink-0 font-normal text-[10px] text-slate-500">
                                    ({objType})
                                  </span>
                                )}
                              </span>
                            );
                          }
                          return (
                            <span className="font-semibold text-xs">{e.label ?? e.logicalId}</span>
                          );
                        })()}
                        <span className="shrink-0 rounded-full bg-white/60 px-2 py-0.5 text-[10px] uppercase tracking-wide">
                          {statusLabel[e.status]}
                        </span>
                      </div>
                      {displayDeltas(e).length > 0 && (
                        <table className="w-full table-fixed border-collapse bg-white/70 text-xs">
                          <colgroup>
                            <col className="w-1/5" />
                            <col className="w-2/5" />
                            <col className="w-2/5" />
                          </colgroup>
                          <thead>
                            <tr className="text-slate-500">
                              <th className="border-slate-200 border-t px-2 py-1 text-left font-medium">
                                {t("gapDiff.colField")}
                              </th>
                              <th className="border-slate-200 border-t px-2 py-1 text-left font-medium">
                                {beforeLabel}
                              </th>
                              <th className="border-slate-200 border-t px-2 py-1 text-left font-medium">
                                {afterLabel}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayDeltas(e).map((d) => (
                              <tr key={d.field} className="align-top">
                                <td className="break-words border-slate-100 border-t px-2 py-1 text-[11px] text-slate-600">
                                  {fieldLabel(d.field)}
                                </td>
                                <td className="break-words border-slate-100 border-t px-2 py-1 text-slate-500 line-through">
                                  {renderCell(d.field, d.before)}
                                </td>
                                <td className="break-words border-slate-100 border-t px-2 py-1 text-slate-800">
                                  {renderCell(d.field, d.after)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {/* Geneste Attribuut/Identifier-wijzigingen (niet bij een
                          verwijderd Concept — dat is dan sowieso weg). */}
                      {tab === "objects" &&
                        e.status !== "deleted" &&
                        (subByParent.get(e.logicalId) ?? []).map((sub) => {
                          const sm = conceptMeta(sub);
                          const subName = sm?.name
                            ? resolveBilingual(sm.name, lang)
                            : (sub.label ?? sub.logicalId);
                          const subType = sm
                            ? conceptTypeLabel(sm.typeId, sm.stereotypes, lang)
                            : "";
                          return (
                            <div
                              key={sub.logicalId}
                              className="border-slate-200 border-t px-2 py-1"
                            >
                              <div className="flex items-center gap-1 text-[11px]">
                                <span
                                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                                  style={{ background: STATUS_DOT[sub.status] }}
                                />
                                <span className="font-medium text-slate-700">
                                  ↳ {subName || sub.logicalId}
                                </span>
                                {subType && (
                                  <span className="text-[10px] text-slate-400">({subType})</span>
                                )}
                              </div>
                              {displayDeltas(sub).map((d) => (
                                <div
                                  key={d.field}
                                  className="break-words pl-4 text-[10px] text-slate-600"
                                >
                                  {fieldLabel(d.field)}:{" "}
                                  {sub.status === "added" ? (
                                    <span>{renderCell(d.field, d.after)}</span>
                                  ) : sub.status === "deleted" ? (
                                    <span className="line-through">
                                      {renderCell(d.field, d.before)}
                                    </span>
                                  ) : (
                                    <span>
                                      <span className="line-through opacity-60">
                                        {renderCell(d.field, d.before)}
                                      </span>{" "}
                                      → {renderCell(d.field, d.after)}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          );
                        })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </ZoomPan>
        </>
      )}
    </div>
  );
}
